import { HowToVideo } from "../HowToVideo";
import { FundraiserInfo } from "./FundraiserInfo";
import { Users, Palette, Link2, Smartphone, BellRing, Truck, Check } from "lucide-react";

const STEPS = [
  [Users, "Create your free coach account", "Then add a squad for each team – U10s, U12 Girls, the senior panel."],
  [Palette, "Set the club look once", "Background, club colours and crest. Every player's shield matches automatically."],
  [Link2, "Share one parent link", "Paste it into the team WhatsApp. No apps or parent accounts needed."],
  [Smartphone, "Parents do the rest", "They tap their child's name, upload a photo, enter the stats and pay for their size."],
  [BellRing, "Track progress", "You get an email and a dashboard update as each shield is finished, and can nudge anyone who hasn't."],
  [Truck, "Printed & delivered", "Every shield is printed in Ireland and delivered in 7–10 working days."],
];

const PARENT = ["Find their child's name on the squad list", "Upload a photo – we remove the background for them", "Add name, position, rating and six stats (0–100)", "Pick a size and pay securely by card"];

const FAQ = [
  ["What does it cost the club?", "Nothing. The coach account is free. Parents pay for their own child's shield, and your club raises €5–€15 on every shield ordered through its squads (see the table above)."],
  ["How long does it take?", "Setting up a squad takes about 5 minutes. Most parents finish their child's shield in under 3 minutes on their phone."],
  ["When do shields arrive?", "Each shield is printed in Ireland and delivered in 7–10 working days after the order is placed."],
  ["Can I check shields before they're printed?", "Yes. You can review, edit or fix any shield in your squad from your dashboard."],
  ["Do parents need an account?", "No. They just open your squad link. Each child's shield can only be edited by the parent who claims it."],
];

export const CoachIntro = () => (
  <div className="space-y-8" data-testid="coach-intro">
    <div>
      <div className="eyebrow">For coaches, clubs & schools</div>
      <h1 className="font-display mt-2 text-4xl font-black uppercase leading-none tracking-tight sm:text-5xl">Run a squad shield fundraiser</h1>
      <p className="mt-4 max-w-xl text-slate-600">Give every player their own personalised shield – their photo, name, club crest, colours and stats on a premium print. You set the club look once, parents add their child and pay, and your club raises funds on every order.</p>
    </div>
    <HowToVideo id="coach" title="Watch: set up a squad fundraiser in 2 minutes" />
    <section aria-labelledby="coach-expect-h">
      <h2 id="coach-expect-h" className="font-display text-xl font-extrabold uppercase">What to expect</h2>
      <ol className="mt-4 space-y-3">
        {STEPS.map(([Icon, t, d], i) => (
          <li key={t} className="flex gap-3" data-testid={`coach-intro-step-${i + 1}`}>
            <span className="font-cond flex h-8 w-8 shrink-0 items-center justify-center bg-slate-900 font-bold text-lime-300">{i + 1}</span>
            <div><div className="flex items-center gap-1.5 font-bold"><Icon className="h-4 w-4" />{t}</div><p className="text-sm text-slate-600">{d}</p></div>
          </li>
        ))}
      </ol>
    </section>
    <section className="border-2 border-slate-200 bg-white p-5" aria-labelledby="coach-parent-h" data-testid="coach-intro-parents">
      <h2 id="coach-parent-h" className="font-display text-lg font-extrabold uppercase">What parents see</h2>
      <ul className="mt-3 grid gap-2 sm:grid-cols-2">
        {PARENT.map((t) => <li key={t} className="flex gap-2 text-sm"><Check className="mt-0.5 h-4 w-4 shrink-0 text-lime-600" />{t}</li>)}
      </ul>
    </section>
    <FundraiserInfo />
    <section aria-labelledby="coach-faq-h">
      <h2 id="coach-faq-h" className="font-display text-xl font-extrabold uppercase">Coach FAQ</h2>
      <div className="mt-3 divide-y border-y-2 border-slate-200">
        {FAQ.map(([q, a], i) => (
          <details key={q} className="group py-3" data-testid={`coach-faq-${i}`}>
            <summary className="cursor-pointer list-none font-bold marker:hidden">{q}<span className="float-right transition-transform group-open:rotate-45">+</span></summary>
            <p className="mt-2 text-sm text-slate-600">{a}</p>
          </details>
        ))}
      </div>
      <a href="/help#coach" className="mt-4 inline-block text-sm font-bold underline" data-testid="coach-intro-video-link">Watch the 2-minute coach video →</a>
    </section>
  </div>
);
